import React, { useCallback, useRef, useState } from 'react';

import Header from '@components/HeaderSearch';
import { SearchFormProvider } from '@components/SearchForm';
import useHotelChainSlug from '@hooks/useHotelChainSlug';
import { usePostMessageListener } from '@hooks/usePostMessageListener';
import CloseIcon from '@mui/icons-material/Close';
import { CircularProgress, IconButton } from '@mui/material';
import { Box, FlexBox } from '@spazious/react-components';
import { useMeasure } from '@spazious/react-hooks';
import {
  ExpandedPayload,
  ExpansionStartedPayload,
  ExpansionStatus,
  IframeEventName,
  PostMessageType,
  ResetCompletedPayload,
  ResetPayload,
} from '@spazious/search-sdk-types';
import postInteractionEvent from '@utils/sdkEvents';

const StandaloneHeader: React.FC = () => {
  const hotelChainSlug = useHotelChainSlug();
  const [containerRef, containerMeasurement] = useMeasure();
  const direction = containerMeasurement.width < 968 ? 'column' : 'row';
  const [offsetTop, setOffsetTop] = useState<string>('0px');

  const [headerWidth, setHeaderWidth] = useState<string>('100%');

  const [showCloseButton, setShowCloseButton] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [expansionStatus, setExpansionStatus] = useState<ExpansionStatus>('idle');

  const headerContainerRef = useRef<HTMLDivElement>(null);

  usePostMessageListener<ExpansionStartedPayload>(
    PostMessageType.EXPANSION_STARTED,
    useCallback(() => {
      const root = document.getElementById('search-widget-root');
      if (root) {
        const width = root.getBoundingClientRect().width;
        setHeaderWidth(`${width}px`);
        setExpansionStatus('expansion_started');
        setOffsetTop('100px');
      }
    }, [])
  );

  // (fullscreen mode)
  usePostMessageListener<ExpandedPayload>(
    PostMessageType.EXPANDED,
    useCallback((payload: any) => {
      if (payload?.fullscreen) {
        setShowCloseButton(true);
      }
      setExpansionStatus('expanded');
    }, [])
  );

  usePostMessageListener<ResetPayload>(
    PostMessageType.RESET,
    useCallback(() => {
      setOffsetTop('0px');
      setShowCloseButton(false);
      setExpansionStatus('resetting');
    }, [])
  );

  usePostMessageListener<ResetCompletedPayload>(
    PostMessageType.RESET_COMPLETED,
    useCallback(() => {
      setOffsetTop('0px');
      setHeaderWidth('100%');
      setShowCloseButton(false);
      setExpansionStatus('idle');
    }, [])
  );

  const handleClose = () => {
    expansionStatus === 'expanded' && postInteractionEvent(IframeEventName.RESET_REQUEST);
  };

  const handleSearchFormReady = useCallback(() => setIsLoading(false), []);

  const isAnyExpanded = expansionStatus === 'expanded' || expansionStatus === 'expansion_started';
  return (
    <SearchFormProvider hotelChainSlug={hotelChainSlug} onReady={handleSearchFormReady}>
      <FlexBox
        ref={headerContainerRef}
        alignItems="center"
        justifyContent="center"
        style={{ position: 'relative', width: '100%' }}
      >
        {/* Conditional rendering of the loader */}
        {isLoading && (
          <Box
            alignItems="center"
            bottom="0"
            display="flex"
            justifyContent="center"
            left="0"
            position="fixed"
            right="0"
            top="0"
            zIndex={99999}
          >
            <CircularProgress color="primary" size={50} />
          </Box>
        )}

        <Box
          bottom={0}
          left={0}
          position="fixed"
          right={0}
          style={{
            backgroundColor: !isAnyExpanded ? 'rgba(255, 0, 0, 0)' : 'rgba(0, 0, 0, 0.44)',
            transition: !isAnyExpanded ? 'background-color 0.8s ease' : '',
            cursor: 'default',
          }}
          top={0}
          onClick={handleClose}
        />

        <Header
          condensed={false}
          containerRef={containerRef}
          direction={direction}
          left="50%"
          position="fixed"
          style={{
            transform: 'translateX(-50%)',
            opacity: isLoading ? 0 : 1,
            transition: expansionStatus !== 'idle' ? 'top 0.4s ease, opacity 0.4s ease' : '',
            top: isAnyExpanded ? offsetTop : '0px',
          }}
          width={headerWidth}
        />

        {showCloseButton && (
          <IconButton
            sx={{
              position: 'fixed',
              top: '40px',
              right: ['20px', '100px'],
              width: ['36px', '48px'],
              height: ['36px', '48px'],
              backgroundColor: 'var(--background-color)',
              zIndex: 10000,
              boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
              '&:hover': { backgroundColor: 'var(--background-color)' },
            }}
            onClick={handleClose}
          >
            <CloseIcon sx={{ fontSize: ['20px', '28px'], color: 'var(--primary-text)' }} />
          </IconButton>
        )}
      </FlexBox>
    </SearchFormProvider>
  );
};

export default StandaloneHeader;
